import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { useTheme } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { FormControl, InputLabel, MenuItem, Select, Snackbar, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import { ViewContext } from '../contexts/viewContext';
import { ConfigContext } from '../contexts/configContext';
import Backend from '../services/wordpressBackend';
import { MarketingContext } from '../contexts/marketingContext';

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<Typography
			component='div'
			role='tabpanel'
			hidden={value !== index}
			id={`action-tabpanel-${index}`}
			aria-labelledby={`action-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</Typography>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

function a11yProps(index) {
	return {
		id: `action-tab-${index}`,
		'aria-controls': `action-tabpanel-${index}`,
	};
}

export default function Screening() {
	const theme = useTheme();
	const [value, setValue] = useState(0);
	const [answers, setAnswers] = useState({
		ontario: '',
		legalStatus: '',
		sourceOfIncome: '',
		onlineBanking: '',
		email: '',
		name: '',
	});

	const [reason, setReason] = useState('');
	const [showCallForm, setShowCallForm] = useState(false);
	const [formSubmitted, setFormSubmitted] = useState(false);
	const [callFormData, setCallFormData] = useState({
		name: '',
		email: '',
		phone: '',
		message: '',
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	const view = useContext(ViewContext);

	useEffect(() => {
		console.log(view);
	}, [view]);

	const [result, setResult] = useState('red'); // green, yellow red

	const config = useContext(ConfigContext);
	const [settings, setSettings] = useState(null);

	const getSettings = useCallback(async configData => {
		setSettings(await Backend.getSettings(configData));
	}, []);

	useEffect(() => {
		if (!settings && config) {
			getSettings(config);
		}
	}, [settings, config, getSettings]);

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	const handleChangeIndex = index => {
		setValue(index);
	};

	const handleEnterNext = key => {
		if (key.code === 'Enter') handleContinue();
	};

	const handleContinue = () => {
		setValue(value => value + 1);
	};

	const handleOntario = answer => {
		setAnswers(prev => ({ ...prev, ontario: answer }));
		setValue(value => value + 1);
	};
	const handleLegalStatus = e => {
		setAnswers(prev => ({ ...prev, legalStatus: e.target.value }));
	};
	const handleSourceOfIncome = answer => {
		setAnswers(prev => ({ ...prev, sourceOfIncome: answer }));
		setValue(value => value + 1);
	};
	const handleOnlineBanking = answer => {
		setAnswers(prev => ({ ...prev, onlineBanking: answer }));
		setValue(value => value + 1);
	};

	const checkAnswers = useCallback(a => {
		let color = 'green';
		const reasons = [];
		if (a.ontario === 'NO') {
			reasons.push('are not currently in the Province of Ontario');
			color = 'red';
		}
		if (a.ontario === 'UNSURE') {
			color = 'yellow';
		}
		if (a.legalStatus === 'UNSURE' || a.legalStatus === 'OTHER') {
			color = 'yellow';
		}

		if (a.sourceOfIncome === 'UNSURE') {
			color = 'yellow';
		}

		if (a.sourceOfIncome === 'NO') {
			reasons.push("don't have a verifible source of income");
			color = 'red';
		}

		if (a.onlineBanking === 'UNSURE') {
			color = 'yellow';
		}

		if (a.onlineBanking === 'NO') {
			reasons.push("don't have an Online Banking enabled account");
			color = 'red';
		}

		switch (reasons.length) {
			case 0:
				setReason('');
				break;
			case 1:
				setReason([reasons[0]]);
				break;
			case 2:
				setReason(`${reasons[0]} and ${reasons[1]}`);
				break;
			case 3:
				setReason(`${reasons[0]}, ${reasons[1]} and ${reasons[2]}`);
				break;
			default:
				setReason('');
				break;
		}

		return color;
	}, []);

	useEffect(() => {
		setResult(checkAnswers(answers));
	}, [answers, checkAnswers]);

	const handleGreenClick = useCallback(() => {
		const url = `https://${settings?.auth0_domain}/authorize?
		response_type=code&
		screen_hint=signup&
		client_id=${settings?.auth0_id}&
		redirect_uri=${settings?.auth0_callback}?sbm=${view?.sbmCookie}`;
		var win = window.open(url, '_blank');
		win.focus();
	}, [settings]);
	const handleYellowClick = useCallback(() => {
		setShowCallForm(true);
	}, []);

	const handleCallFormChange = e => {
		const { name, value } = e.target;
		setCallFormData(prev => ({ ...prev, [name]: value }));
	};

	const handleCallFormSubmit = async e => {
		e.preventDefault();
		setIsSubmitting(true);
		try {
			// Helper function to format legal status display value
			const getLegalStatusLabel = (value) => {
				const statusMap = {
					'CITIZEN': 'Citizen',
					'PERMANENT': 'Permanent Resident',
					'WORK-PERMIT': 'Foreign Worker (Work Permit Holder)',
					'STUDENT': 'Student (Study Permit Holder)',
					'REFUGEE': 'Refugee (Refugee Protection Claimant Document or Resolution from the Immigration and Refugee Board of Canada granting Refugee Status Holder)',
					'OTHER': 'Other',
					'UNSURE': 'Unsure',
				};
				return statusMap[value] || value;
			};

			// Format screening answers as question/answer array
			const screeningQuestions = [
				{
					question: 'Are you currently in the Province of Ontario, Canada?',
					answer: answers.ontario || 'Not answered'
				},
				{
					question: 'What is your legal status in Canada?',
					answer: getLegalStatusLabel(answers.legalStatus) || 'Not answered'
				},
				{
					question: 'Do you have a verifiable source of income?',
					answer: answers.sourceOfIncome || 'Not answered'
				},
				{
					question: 'Do you have an Online Banking enabled account with a Bank, Credit Union, or other, to your name?',
					answer: answers.onlineBanking || 'Not answered'
				},
				{
					question: 'Phone Number',
					answer: callFormData.phone
				}
			];

			// Add additional message if provided
			if (callFormData.message) {
				screeningQuestions.push({
					question: 'Additional Information',
					answer: callFormData.message
				});
			}

			const response = await fetch('https://hammurabi-app.herokuapp.com/contact/submit', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Contact-API-Key': 'SyFlsVOQykjcPwYxBgCDEKgGILxcCtiFpx',
				},
				body: JSON.stringify({
					name: callFormData.name,
					email: callFormData.email,
					message: screeningQuestions,
				}),
			});
			const data = await response.json();
			if (response.ok) {
				setSnackbar({ open: true, message: 'Your request has been submitted successfully!', severity: 'success' });
				setShowCallForm(false);
				setFormSubmitted(true);
				setCallFormData({ name: '', email: '', phone: '', message: '' });
			} else {
				setSnackbar({ open: true, message: data.error || 'There was an error submitting your request.', severity: 'error' });
			}
		} catch (error) {
			console.error('Error submitting form:', error);
			setSnackbar({ open: true, message: 'There was an error submitting your request. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};
	const handleRedClick = useCallback(() => {
		var win = window.open(settings?.dont_apply_url, '_blank');
		win.focus();
	}, [settings]);

	return (
		<div>
			<Tabs
				value={value}
				onChange={handleChange}
				indicatorColor='primary'
				textColor='primary'
				variant='fullWidth'
				aria-label='action tabs example'
			>
				<Tab label='' disabled {...a11yProps(0)} />
				<Tab label='' disabled {...a11yProps(1)} />
				<Tab label='' disabled {...a11yProps(2)} />
				<Tab label='' disabled {...a11yProps(3)} />
				<Tab label='' disabled {...a11yProps(4)} />
			</Tabs>

			<SwipeableViews
				axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
				index={value}
				onChangeIndex={handleChangeIndex}
			>
				<TabPanel value={value} index={0} dir={theme.direction}>
					<div className='text-center text-3xl font-bold my-16'>
						Are you currently in the Province of Ontario, Canada?
					</div>
					<div className='flex items-center justify-center gap-8 mb-8'>
						<div
							onClick={() => handleOntario('YES')}
							className='bg-[#90FFBE] p-4 rounded-full w-32 text-center cursor-pointer'
						>
							YES
						</div>
						<div
							onClick={() => handleOntario('NO')}
							className='bg-[#86CDFF] p-4 rounded-full w-32 text-center cursor-pointer text-white'
						>
							NO
						</div>
						<div
							onClick={() => handleOntario('UNSURE')}
							className='bg-[#606060] p-4 rounded-full w-32 text-center text-white cursor-pointer'
						>
							UNSURE
						</div>
					</div>
				</TabPanel>
				<TabPanel value={value} index={1} dir={theme.direction}>
					<div className='text-center text-3xl font-bold my-16'>What is your legal status in Canada?</div>
					<div className='mb-8'>
						<FormControl fullWidth>
							<InputLabel id='demo-simple-select-label'>Legal Status</InputLabel>
							<Select
								labelId='demo-simple-select-label'
								id='demo-simple-select'
								value={answers.legalStatus}
								label='Legal Status'
								onChange={handleLegalStatus}
								onKeyDown={handleEnterNext}
							>
								<MenuItem value={''}>Select one</MenuItem>
								<MenuItem value={'CITIZEN'}>Citizen</MenuItem>
								<MenuItem value={'PERMANENT'}>Permanent Resident</MenuItem>
								<MenuItem value={'WORK-PERMIT'}>Foreign Worker (Work Permit Holder)</MenuItem>
								<MenuItem value={'STUDENT'}>Student (Study Permit Holder)</MenuItem>
								<MenuItem
									value={'REFUGEE'}
									style={{
										whiteSpace: 'unset',
										wordBreak: 'break-all',
									}}
								>
									Refugee (Refugee Protection Claimant Document or Resolution from the Immigration and Refugee
									Board of Canada granting Refugee Status Holder)
								</MenuItem>
								<MenuItem value={'OTHER'}>Other</MenuItem>
								<MenuItem value={'UNSURE'}>Unsure</MenuItem>
							</Select>
						</FormControl>
					</div>
					<div className='flex items-center justify-center gap-8'>
						<div
							onClick={handleContinue}
							className='bg-[#90FFBE] p-4 rounded-full w-32 text-center cursor-pointer mb-4'
						>
							CONTINUE
						</div>
					</div>
					<div
						onClick={() => setValue(value => value - 1)}
						className='text-gray-400 uppercase text-xs mx-auto w-14 cursor-pointer'
					>
						go back
					</div>
				</TabPanel>
				<TabPanel value={value} index={2} dir={theme.direction}>
					<div className='text-center text-3xl font-bold my-16'>Do you have a verifiable source of income?</div>
					<div className='flex items-center justify-center gap-8 mb-8'>
						<div
							onClick={() => handleSourceOfIncome('YES')}
							className='bg-[#90FFBE] p-4 rounded-full w-32 text-center cursor-pointer'
						>
							YES
						</div>
						<div
							onClick={() => handleSourceOfIncome('NO')}
							className='bg-[#86CDFF] p-4 rounded-full w-32 text-center cursor-pointer text-white'
						>
							NO
						</div>
						<div
							onClick={() => handleSourceOfIncome('UNSURE')}
							className='bg-[#606060] p-4 rounded-full w-32 text-center text-white cursor-pointer'
						>
							UNSURE
						</div>
					</div>
					<div
						onClick={() => setValue(value => value - 1)}
						className='text-gray-400 uppercase text-xs mx-auto w-14 cursor-pointer'
					>
						go back
					</div>
				</TabPanel>
				<TabPanel value={value} index={3} dir={theme.direction}>
					<div className='text-center text-3xl font-bold my-16'>
						Do you have an Online Banking enabled account with a Bank, Credit Union, or other, to your name?
					</div>
					<div className='flex items-center justify-center gap-8 mb-8'>
						<div
							onClick={() => handleOnlineBanking('YES')}
							className='bg-[#90FFBE] p-4 rounded-full w-32 text-center cursor-pointer'
						>
							YES
						</div>
						<div
							onClick={() => handleOnlineBanking('NO')}
							className='bg-[#86CDFF] p-4 rounded-full w-32 text-center cursor-pointer text-white'
						>
							NO
						</div>
						<div
							onClick={() => handleOnlineBanking('UNSURE')}
							className='bg-[#606060] p-4 rounded-full w-32 text-center text-white cursor-pointer'
						>
							UNSURE
						</div>
					</div>
					<div
						onClick={() => setValue(value => value - 1)}
						className='text-gray-400 uppercase text-xs mx-auto w-14 cursor-pointer'
					>
						go back
					</div>
				</TabPanel>
				<TabPanel value={value} index={4} dir={theme.direction}>
					<div>
						{result === 'green' ? (
							<div className='text-center my-16 flex items-center flex-col'>
								<div className='text-2xl font-bold'>
									<CheckCircleIcon className='text-green-400' fontSize='large' />
								</div>
								<div className='text-2xl font-bold'>You are eligible to apply for our products!</div>
								<div>
									To proceed with a loan application, we need you to sign up for an account in our Customer
									Portal. Just click the button below.
								</div>
								<div
									onClick={handleGreenClick}
									className='bg-gradient-to-r from-[#90FFBE] to-[#86CDFF] hover:text-white my-4 p-4 rounded-full w-32 text-center cursor-pointer shadow-lg hover:bg-black '
								>
									SIGNUP
								</div>
							</div>
						) : result === 'yellow' ? (
							formSubmitted ? (
								<div className='text-center my-16 flex items-center flex-col'>
									<div className='text-2xl font-bold'>
										<CheckCircleIcon className='text-green-400' fontSize='large' />
									</div>
									<div className='text-2xl font-bold'>
										Thank you! Your information has been submitted successfully.
									</div>
									<div className='mt-4'>
										A member of our team will review your information and contact you shortly to discuss your eligibility.
									</div>
								</div>
							) : (
								<div className='text-center my-16 flex items-center flex-col'>
									<div className='text-2xl font-bold'>
										<ReportIcon className='text-yellow-400' fontSize='large' />
									</div>
									<div className='text-2xl font-bold'>
										At this time, we can't asses your eligibility without obtaining further information.
									</div>
									<div>Would you like to schedule a phone call with a member of our team?</div>
									<div
										onClick={handleYellowClick}
										className='bg-gradient-to-r from-[#90FFBE] to-[#86CDFF] hover:text-white my-4 p-4 rounded-full w-64 uppercase text-center cursor-pointer shadow-lg hover:bg-black '
									>
										Request a call
									</div>
								</div>
							)
						) : (
							<div className='text-center my-16 flex items-center flex-col'>
								<div className='text-2xl font-bold'>
									<ReportIcon className='text-yellow-400' fontSize='large' />
								</div>
								<div className='text-2xl font-bold'>
									Unfortunately, based on your answers you are not eligible for our products.
								</div>
								<div>Given that you {reason}.</div>
								<div>
									Please keep us in mind if your circumstances change and we will gladly consider you in the
									future.
								</div>
								<div
									onClick={handleRedClick}
									className='bg-gradient-to-r from-[#90FFBE] to-[#86CDFF] hover:text-white my-4 py-4 px-8 rounded-full text-[#606060] uppercase text-center cursor-pointer shadow-lg hover:bg-black '
								>
									Subscribe to our Financial Education Content
								</div>
							</div>
						)}
					</div>
					<div
						onClick={() => setValue(value => value - 1)}
						className='text-gray-400 uppercase text-xs mx-auto w-14 cursor-pointer'
					>
						go back
					</div>
				</TabPanel>
			</SwipeableViews>

			{/* Modal de Request a Call */}
			{showCallForm && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-white rounded-lg p-8 max-w-md w-full mx-4 relative'>
						<button
							onClick={() => setShowCallForm(false)}
							className='absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl'
						>
							×
						</button>
						<h2 className='text-2xl font-bold mb-6 text-center'>Request a Call</h2>
						<form onSubmit={handleCallFormSubmit} className='space-y-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Name</label>
								<input
									type='text'
									name='name'
									value={callFormData.name}
									onChange={handleCallFormChange}
									required
									className='w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
									placeholder='Your name'
								/>
							</div>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Email</label>
								<input
									type='email'
									name='email'
									value={callFormData.email}
									onChange={handleCallFormChange}
									required
									className='w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
									placeholder='your@email.com'
								/>
							</div>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Phone</label>
								<input
									type='tel'
									name='phone'
									value={callFormData.phone}
									onChange={handleCallFormChange}
									required
									className='w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
									placeholder='Your phone number'
								/>
							</div>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Message</label>
								<textarea
									name='message'
									value={callFormData.message}
									onChange={handleCallFormChange}
									rows={4}
									className='w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
									placeholder='How can we help you?'
								/>
							</div>
							<button
								type='submit'
								disabled={isSubmitting}
								className='w-full bg-gradient-to-r from-[#90FFBE] to-[#86CDFF] p-4 rounded-full uppercase font-medium hover:opacity-90 disabled:opacity-50'
							>
								{isSubmitting ? 'Sending...' : 'Submit'}
							</button>
						</form>
					</div>
				</div>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
